import React, { useState } from 'react';
import {
  Button, Checkbox, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, FormControl, FormLabel, Input, Textarea, Stack, Switch, useToast,
  Box, Text, HStack, InputGroup, InputRightElement, useClipboard, Link, Badge,
  Menu, MenuButton, MenuList, MenuItem, MenuDivider, Spacer,
} from '@chakra-ui/react';
import {
  FiSend, FiExternalLink, FiEye, FiUserPlus,
} from 'react-icons/fi';
import { useConfirm } from '../ConfirmProvider';
import { openDocumentPdf } from './ViewPdfButton'; // item 1249 — preview-at-send
import {
  sendDocument, getInvoicePayLink, getDocumentRecipient, getDocumentCopy,
  getDocumentRecipients, CrmRecipient,
} from '../../hooks/useSendDocument';

// item 111 — "Send" a document (invoice / proposal / PO) to the client by email, with CC.
// item 32 — invoices can include a (stub) Stripe pay link. item 131 — proposals can carry
// the public accept link, passed in via `extraLink`.
interface Props {
  documentType: 'Invoice' | 'Proposal' | 'PurchaseOrder' | 'WorkOrder' | 'SalesOrder';
  documentId: string;
  documentLabel?: string;
  defaultTo?: string;
  // item 552 — per-type default subject/message (e.g. the proposal's number + a warm note);
  // fall back to the generic copy when not supplied.
  defaultSubject?: string;
  defaultMessage?: string;
  // a link to embed in the body (e.g. proposal public accept link, item 131)
  extraLink?: { label: string; url: string };
  // item 434 — bump this counter to open the dialog programmatically (Finalize & send)
  autoOpenSignal?: number;
  // item 550 — render ONLY the modal (no visible Send button); used when a list row opens
  // the send dialog programmatically (e.g. proposal "Send to client").
  hideTrigger?: boolean;
  // item 553 — fired after a successful send (e.g. a PO flips to "Ordered" once sent)
  onSent?: () => void;
  // item 799 (Chad) — send WITHOUT the document PDF (client "order placed" confirmation must
  // not carry the vendor PO / its distributor costs). Also relabels the modal + default copy.
  noAttachment?: boolean;
  modalTitle?: string;
  // item 1474 — whose CRM addresses populate the picker (default: the document's own contact;
  // 'customer' = the PO's project client for the order-placed notice)
  recipientAudience?: 'customer';
}

// item 1430 (Chad: "what setting do I change for invoices to never go to sales") — item 553's
// TEMP test-mode auto-CC of sales@ was still here, pre-filling the CC box on EVERY document send.
// Removed: the box starts blank. The guaranteed copies (sales@ on quotes, ops/payments streams)
// are applied server-side by SalesCcPolicy and edited under Settings → Email routing.

// item 1247 — human-readable type names for subjects/confirms ("work order", not "workorder")
const TYPE_LABEL: Record<string, string> = {
  Invoice: 'invoice',
  Proposal: 'proposal',
  PurchaseOrder: 'purchase order',
  WorkOrder: 'work order',
  SalesOrder: 'sales order',
};

// item 1249 (Chad: "no way to preview at send") — the exact PDF the send will attach,
// previewable from inside the dialog. Same endpoints the forms' Preview buttons hit.
const PDF_PATH: Record<string, (id: string) => string> = {
  Invoice: (id) => `invoice/${id}/pdf`,
  Proposal: (id) => `proposal/${id}/pdf`,
  PurchaseOrder: (id) => `PurchaseOrder/${id}/pdf`,
  WorkOrder: (id) => `WorkOrder/${id}/pdf`,
  SalesOrder: (id) => `WorkOrder/${id}/pdf`,
};

const SendDocumentButton: React.FC<Props> = ({
  documentType, documentId, documentLabel, defaultTo, defaultSubject, defaultMessage,
  extraLink, autoOpenSignal, hideTrigger, onSent, noAttachment, modalTitle,
  recipientAudience,
}) => {
  const confirm = useConfirm();
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [to, setTo] = useState(defaultTo ?? '');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(`Your ${TYPE_LABEL[documentType] ?? documentType.toLowerCase()}${documentLabel ? ` ${documentLabel}` : ''}`);
  const [body, setBody] = useState('Please find your document attached.');
  // item 540 — mirror recurring revenue: for an invoice the pay-now link is ON by default
  // (ACH always offered), card stays opt-in. The whole block only renders for invoices.
  const [includePay, setIncludePay] = useState(documentType === 'Invoice');
  // item 298 — card is opt-in PER SEND (resets every time); ACH debit is always offered
  const [allowCard, setAllowCard] = useState(false);
  const [sending, setSending] = useState(false);
  // item 1327 — the CRM's addresses for this document's customer, for the To picker
  const [crm, setCrm] = useState<CrmRecipient[]>([]);
  // item 540 — the minted Stripe link, surfaced AFTER send so you actually get the test
  // link (previously it was only attached to the outgoing email — invisible to the sender).
  const [sentLink, setSentLink] = useState<string | null>(null);
  const { hasCopied, onCopy } = useClipboard(sentLink ?? '');
  // real Stripe checkout links are test links here (env uses an sk_test_ key); the stub
  // gateway (Stripe not configured) returns a *.stub URL — call that out.
  const isStub = !!sentLink && !sentLink.includes('stripe.com');

  // item 318 — when the dialog opens with no recipient yet, ask the server for the routed
  // address (customer accounting email / assigned staff / vendor orders email) and pre-fill it.
  const closeDialog = () => { setIsOpen(false); setSentLink(null); };

  const openDialog = async () => {
    setIsOpen(true);
    setSentLink(null);
    // item 552 — each open uses the latest per-type copy (props may change per row), falling
    // back to the generic subject/message.
    setSubject(defaultSubject ?? `Your ${TYPE_LABEL[documentType] ?? documentType.toLowerCase()}${documentLabel ? ` ${documentLabel}` : ''}`);
    setBody(defaultMessage ?? 'Please find your document attached.');
    setCc(''); // item 1430 — no auto-CC; the sender adds one when wanted
    // item 576 — the status-aware copy rule lives server-side (ONE rule per doc type, keyed
    // off the document's status — see DocumentEmailCopy); pre-fill whatever the caller didn't
    // explicitly supply so every entry point previews the copy the backend would compose.
    if (!defaultSubject || !defaultMessage) {
      getDocumentCopy(documentType, documentId).then((copy) => {
        if (copy?.subject && !defaultSubject) setSubject(copy.subject);
        if (copy?.body && !defaultMessage) setBody(copy.body);
      });
    }
    // item 553 — resolve the recipient for THIS document on EVERY open; never carry the last
    // row's address. The hidden dialog is reused across rows, so a persisted `to` would default
    // one client's proposal to a different client's email. defaultTo wins when explicitly given.
    const seed = (defaultTo ?? '').trim();
    setTo(seed);
    // item 1327 — load the pick list alongside the routed default; a failure here must never
    // block the dialog, so it is deliberately not awaited with the recipient resolve.
    setCrm([]);
    getDocumentRecipients(documentType, documentId, recipientAudience).then(setCrm);
    if (!seed) {
      const routed = await getDocumentRecipient(documentType, documentId);
      if (routed) setTo(routed);
    }
  };

  // item 1327 — append a picked address to whatever is already typed, without duplicating it.
  // The field stays free text: picking is a shortcut, never a restriction.
  const addRecipient = (email: string) => {
    setTo((current) => {
      const parts = current.split(',').map((x) => x.trim()).filter(Boolean);
      if (parts.some((x) => x.toLowerCase() === email.toLowerCase())) return current;
      return [...parts, email].join(', ');
    });
  };

  // item 434 — Finalize & send bumps the signal; open the dialog as if clicked
  React.useEffect(() => {
    if (autoOpenSignal && autoOpenSignal > 0) { openDialog(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenSignal]);

  const send = async () => {
    if (!to.trim()) {
      toast({ status: 'warning', title: 'A recipient email is required.' });
      return;
    }
    // item 550 — confirm before every send. item 551 — accounting is NOT auto-copied on
    // client sends; the sender controls CC here.
    const ccList = cc.split(',').map((c) => c.trim()).filter(Boolean);
    // eslint-disable-next-line no-alert
    if (!(await confirm(`Send this ${TYPE_LABEL[documentType] ?? documentType.toLowerCase()} to ${to.trim()}${ccList.length ? ` (cc ${ccList.join(', ')})` : ''}?`, { title: 'Send document', confirmLabel: 'Send' }))) return;
    setSending(true);
    try {
      let payLink: string | undefined;
      if (includePay && documentType === 'Invoice') {
        // item 1034 — the backend now REFUSES to mint a link for an already-paid invoice
        // (double-payment guard). A resend of a paid invoice still goes out — just without
        // the pay link — instead of failing the whole send.
        try {
          payLink = await getInvoicePayLink(documentId, allowCard);
        } catch (payErr: any) {
          if (payErr?.response?.status === 400) {
            toast({
              status: 'info',
              title: 'No pay link included',
              description: payErr?.response?.data ?? 'This invoice is already paid.',
            });
          } else throw payErr;
        }
      }
      // item 553 — the accept link rides as a structured action (label + url) so the branded
      // email renders it as an "Approve proposal" button, not a long raw URL in the body.
      await sendDocument({
        to: to.trim(),
        cc: ccList,
        subject,
        body,
        documentType,
        documentId,
        payLink,
        actionLabel: extraLink?.label,
        actionUrl: extraLink?.url,
        noAttachment,
      });
      toast({ status: 'success', title: 'Sent', description: `Emailed to ${to.trim()}` });
      if (onSent) onSent(); // item 553 — e.g. flip a PO to "Ordered"
      // item 540 — surface the pay link so you get the test link in hand; keep the dialog
      // open showing it. No link (e.g. pay-now off) → just close.
      if (payLink) setSentLink(payLink); else closeDialog();
    } catch (e: any) {
      toast({ status: 'error', title: 'Send failed', description: e?.response?.data ?? 'Try again.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!hideTrigger && (
        <Button size="sm" variant="ghost" leftIcon={<FiSend />} onClick={openDialog}>
          Send
        </Button>
      )}
      <Modal isOpen={isOpen} onClose={closeDialog} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{modalTitle ?? `Send ${TYPE_LABEL[documentType] ?? documentType.toLowerCase()} to client`}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={3}>
              <FormControl isRequired>
                <HStack spacing={2} align="center" mb={1}>
                  <FormLabel fontSize="sm" mb={0}>To</FormLabel>
                  <Spacer />
                  {/* item 1327 (Chad) — pick from the customer's CRM contacts instead of typing
                      addresses by hand. Hidden entirely when the CRM has nothing to offer, so
                      the dialog never shows an empty menu. */}
                  {crm.length > 0 && (
                    <Menu placement="bottom-end" isLazy>
                      <MenuButton
                        as={Button}
                        size="xs"
                        variant="ghost"
                        leftIcon={<FiUserPlus />}
                      >
                        Add from CRM
                      </MenuButton>
                      <MenuList maxH="260px" overflowY="auto" zIndex={1500}>
                        {crm.map((r, i) => {
                          const already = to.split(',').some(
                            (x) => x.trim().toLowerCase() === r.email.toLowerCase(),
                          );
                          return (
                            <React.Fragment key={r.email}>
                              {i > 0 && <MenuDivider my={0} />}
                              <MenuItem
                                onClick={() => addRecipient(r.email)}
                                isDisabled={already}
                              >
                                <Box>
                                  <HStack spacing={2}>
                                    <Text fontSize="sm" fontWeight="600">{r.name}</Text>
                                    <Badge fontSize="0.6rem" colorScheme="gray">{r.label}</Badge>
                                    {already && (
                                      <Badge fontSize="0.6rem" colorScheme="green">added</Badge>
                                    )}
                                  </HStack>
                                  <Text fontSize="xs" color="gray.500">{r.email}</Text>
                                </Box>
                              </MenuItem>
                            </React.Fragment>
                          );
                        })}
                      </MenuList>
                    </Menu>
                  )}
                </HStack>
                <Input size="sm" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Separate multiple with commas" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">CC</FormLabel>
                <Input size="sm" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Optional — separate multiple with commas" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Subject</FormLabel>
                <Input size="sm" value={subject} onChange={(e) => setSubject(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Message</FormLabel>
                <Textarea size="sm" rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
              </FormControl>
              {/* item 555 — show the embedded action link (e.g. the proposal approval link) so
                  the sender can EYEBALL it before sending. A localhost link is flagged loudly —
                  it's dead for the client (must be the deployed public site). */}
              {extraLink?.url && (
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={3}
                  borderColor={extraLink.url.includes('localhost') ? 'orange.400' : undefined}
                >
                  <HStack mb={1} spacing={2}>
                    <Text fontSize="sm" fontWeight="semibold">{`${extraLink.label} link`}</Text>
                    {extraLink.url.includes('localhost') && (
                      <Badge colorScheme="orange">LOCALHOST — the client can’t open this</Badge>
                    )}
                  </HStack>
                  <Text fontSize="xs" opacity={0.8} wordBreak="break-all">{extraLink.url}</Text>
                </Box>
              )}
              {documentType === 'Invoice' && (
                <>
                  <Switch isChecked={includePay} onChange={(e) => { setIncludePay(e.target.checked); if (!e.target.checked) setAllowCard(false); }}>
                    Include a pay-now link (Stripe)
                  </Switch>
                  {includePay && (
                    <Stack pl={6} spacing={1}>
                      <Checkbox size="sm" isChecked isDisabled>
                        ACH bank debit (always offered)
                      </Checkbox>
                      <Checkbox size="sm" isChecked={allowCard} onChange={(e) => setAllowCard(e.target.checked)}>
                        Also allow card payment — this send only
                      </Checkbox>
                    </Stack>
                  )}
                </>
              )}
              {/* item 540 — the minted pay link, shown so you actually get the test link */}
              {sentLink && (
                <Box borderWidth="1px" borderRadius="md" p={3} mt={1}>
                  <HStack mb={2} spacing={2}>
                    <Text fontSize="sm" fontWeight="semibold">Pay-now link</Text>
                    <Badge colorScheme={isStub ? 'orange' : 'green'}>{isStub ? 'STUB' : 'STRIPE TEST'}</Badge>
                  </HStack>
                  <InputGroup size="sm">
                    <Input readOnly value={sentLink} pr="4rem" fontSize="xs" />
                    <InputRightElement width="4rem">
                      <Button size="xs" onClick={onCopy}>{hasCopied ? 'Copied' : 'Copy'}</Button>
                    </InputRightElement>
                  </InputGroup>
                  <Link href={sentLink} isExternal fontSize="xs" color="brandGreen.400" mt={2} display="inline-flex" alignItems="center">
                    Open link
                    <Box as={FiExternalLink} ml={1} />
                  </Link>
                  {isStub && (
                    <Text fontSize="xs" color="orange.400" mt={1}>
                      Stripe isn’t configured on this environment — this is a placeholder link
                      (set BASIX_STRIPE_SECRET to mint real test links).
                    </Text>
                  )}
                </Box>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter>
            {sentLink ? (
              <Button onClick={closeDialog}>Done</Button>
            ) : (
              <>
                {/* item 1249 — see exactly what will be attached before committing to Send */}
                {!noAttachment && PDF_PATH[documentType] && (
                  <Button
                    variant="ghost"
                    leftIcon={<FiEye />}
                    mr="auto"
                    onClick={() => openDocumentPdf(PDF_PATH[documentType](documentId), true).catch(() => toast({
                      status: 'error', title: 'Could not open the PDF', duration: 6000, isClosable: true,
                    }))}
                  >
                    Preview PDF
                  </Button>
                )}
                <Button variant="ghost" mr={3} onClick={closeDialog}>Cancel</Button>
                <Button leftIcon={<FiSend />} isLoading={sending} onClick={send}>Send</Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

SendDocumentButton.defaultProps = {
  recipientAudience: undefined,
  autoOpenSignal: undefined,
  documentLabel: undefined,
  defaultTo: undefined,
  defaultSubject: undefined,
  defaultMessage: undefined,
  extraLink: undefined,
  noAttachment: false,
  modalTitle: undefined,
  hideTrigger: false,
  onSent: undefined,
};

export default SendDocumentButton;
